import { getObservableT } from "../Infra/i18next";
import { FormModel } from "./FormModel";
import { validators } from "../Logic/Validators";
import { getAnonymousUserId } from "../Logic/AuthManager";
import { getHeapGlobalEventProperties } from "../Infra/LoadHeap";
import { PatientAnalyticsEvent } from "healthex-common/analytics.dtos";
const t = getObservableT("patients");
export class AccountSetupCompletionManager {
    constructor(authManager, socialLoginAuthManager, analyticsService) {
        this.ageValidationErrorText = t("You must be at least 18 years old to use HealthEx");
        this.formModel = new FormModel({
            firstName: "",
            lastName: "",
            birthday: "",
            mobilePhone: "",
        }, []);
        this.authManager = authManager;
        this.socialLoginAuthManager = socialLoginAuthManager;
        this.analyticsService = analyticsService;
    }
    get pendingData() {
        return this.socialLoginAuthManager.pendingSocialLoginData;
    }
    get isFirstTimeVerification() {
        const pendingData = this.pendingData;
        return !!pendingData && !pendingData.isExistingUser;
    }
    trackDemographicsUpdatedIfFirstTime() {
        var _a;
        if (!this.isFirstTimeVerification) {
            return;
        }
        this.trackAnalyticsEvent(PatientAnalyticsEvent.PATIENT_DEMOGRAPHICS_UPDATED, (_a = this.pendingData) === null || _a === void 0 ? void 0 : _a.gfidProvider);
    }
    trackMobileVerified(isFirstTimeLogin, gfidProvider) {
        if (!isFirstTimeLogin) {
            return;
        }
        this.trackAnalyticsEvent(PatientAnalyticsEvent.PATIENT_MOBILE_VERIFIED, gfidProvider);
    }
    trackAnalyticsEvent(event, gfidProvider) {
        var _a, _b;
        void this.analyticsService.trackHeapEvent({
            event,
            user_id: (_a = getAnonymousUserId()) !== null && _a !== void 0 ? _a : undefined,
            identity: (_b = this.authManager.id) !== null && _b !== void 0 ? _b : undefined,
            properties: {
                ...getHeapGlobalEventProperties(),
                provider: gfidProvider,
            },
        });
    }
    get shouldShowPhoneOnlyScreen() {
        const pending = this.pendingData;
        if (!pending)
            return false;
        const fieldsCompleted = !!pending.firstName && !!pending.lastName && !!pending.dateOfBirth && !!pending.phoneNumber;
        return pending.isExistingUser && pending.showAccountSetup && fieldsCompleted;
    }
    get isPhoneVerified() {
        const pending = this.pendingData;
        if (pending === null || pending === void 0 ? void 0 : pending.phoneVerified)
            return true;
        return this.authManager.phoneVerified;
    }
    get hasAgeValidationError() {
        return this.formModel.errors.birthday === this.ageValidationErrorText;
    }
    get input() {
        return this.formModel.input;
    }
    prepopulate(patientInfo) {
        const pendingData = this.pendingData;
        if (this.authManager.isIAL2Authorized && patientInfo) {
            const firstName = patientInfo.firstName || (pendingData === null || pendingData === void 0 ? void 0 : pendingData.firstName);
            const lastName = patientInfo.lastName || (pendingData === null || pendingData === void 0 ? void 0 : pendingData.lastName);
            const birthday = patientInfo.dateOfBirth || (pendingData === null || pendingData === void 0 ? void 0 : pendingData.dateOfBirth);
            const mobilePhone = patientInfo.phone || (pendingData === null || pendingData === void 0 ? void 0 : pendingData.phoneNumber);
            if (firstName)
                this.formModel.updateInput({ firstName }, false);
            if (lastName)
                this.formModel.updateInput({ lastName }, false);
            if (birthday)
                this.formModel.updateInput({ birthday }, false);
            if (mobilePhone)
                this.formModel.updateInput({ mobilePhone }, false);
            return;
        }
        if (pendingData) {
            if (pendingData.firstName)
                this.formModel.updateInput({ firstName: pendingData.firstName }, false);
            if (pendingData.lastName)
                this.formModel.updateInput({ lastName: pendingData.lastName }, false);
            if (pendingData.dateOfBirth)
                this.formModel.updateInput({ birthday: pendingData.dateOfBirth }, false);
            if (pendingData.phoneNumber)
                this.formModel.updateInput({ mobilePhone: pendingData.phoneNumber }, false);
            return;
        }
        if (!patientInfo)
            return;
        if (patientInfo.firstName)
            this.formModel.updateInput({ firstName: patientInfo.firstName }, false);
        if (patientInfo.lastName)
            this.formModel.updateInput({ lastName: patientInfo.lastName }, false);
        if (patientInfo.dateOfBirth)
            this.formModel.updateInput({ birthday: patientInfo.dateOfBirth }, false);
        if (patientInfo.phone)
            this.formModel.updateInput({ mobilePhone: patientInfo.phone }, false);
    }
    validateInput() {
        const { notBlank, validPhone, pastDate, atLeast18YearsOld } = validators;
        const f = this.formModel;
        f.clearErrors();
        if (!this.shouldShowPhoneOnlyScreen) {
            f.validateField("firstName", notBlank, t("Enter your first name"));
            f.validateField("lastName", notBlank, t("Enter your last name"));
            f.validateField("birthday", notBlank, t("Enter your birthday"));
            f.validateField("birthday", pastDate, t("Enter a valid birthday"));
            f.validateField("birthday", atLeast18YearsOld, this.ageValidationErrorText);
        }
        f.validateField("mobilePhone", notBlank, t("Enter your phone number"));
        f.validateField("mobilePhone", validPhone, t("Please enter a valid US phone number to continue"));
        return !f.hasErrors();
    }
}
